// University of California, San Diego: public college page (#2596), official
// facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UC San Diego's Common Data Set 2025-2026 (CDS-2025-2026-Final4.pdf,
//    Institutional Research). C1 gives Fall 2025 applicants, admits and
//    enrollees with a residency table. C8 marks the SAT and ACT "not
//    considered for admission, even if submitted" for Fall 2027. C21 and C22
//    answer no to early decision and early action.
//  - UC San Diego admissions pages (first-year requirements, timeline) and
//    the UC systemwide first-year dates page.
//  - Financial Aid and Scholarships, undergraduate cost of attendance for the
//    Fall 2026 cohort, 2026-27 (in-state and nonresident totals as published).
//  - College Scorecard, school id 110680, read from the Scorecard school
//    page (the API's demo key was over its rate limit; same data).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UC San Diego Common Data Set 2025-2026',
        url: 'https://ir.ucsd.edu/stats/undergrad/CDS-2025-2026-Final4.pdf',
        read: '2026-09-30',
    },
    timeline: {
        label: 'UC San Diego Admissions, First-Year Application Timeline',
        url: 'https://admissions.ucsd.edu/first-year/application-timeline.html',
        read: '2026-09-30',
    },
    ucDates: {
        label: 'UC Admissions, First-Year Dates and Deadlines',
        url: 'https://admission.universityofcalifornia.edu/how-to-apply/applying-as-a-first-year/dates-and-deadlines.html',
        read: '2026-09-30',
    },
    requirements: {
        label: 'UC San Diego Admissions, First-Year Application Requirements',
        url: 'https://admissions.ucsd.edu/first-year/application-requirements.html',
        read: '2026-09-30',
    },
    coa: {
        label: 'UC San Diego Financial Aid, Undergraduate Cost of Attendance',
        url: 'https://fas.ucsd.edu/cost-of-attendance/undergraduates/index.html',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110680-University-of-California-San-Diego',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-california-san-diego',
    kbId: 'university_of_california_san_diego',
    name: 'University of California, San Diego',
    shortName: 'UC San Diego',
    location: 'La Jolla, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UC San Diego brand guidelines); decoration only.
    branding: {
        primary: '#182B49',
        secondary: '#C69214',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_san_diego_1_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 136750,
        admitted: 39054,
        enrolled: 7801,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 87584, admitted: 21677 },
            { group: 'Out-of-state', applied: 24912, admitted: 9881 },
            { group: 'International', applied: 24254, admitted: 7496 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UC San Diego\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UC San Diego offers neither. The Common Data Set answers no to both.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Some majors ask for more, or admit fewer students than apply:',
        rows: [
            { school: 'Data Science, Engineering, Public Health', requirement: 'Selective majors, with limited space. UC San Diego strongly recommends listing an alternate major that is not selective; you may be admitted to it if space is available.' },
            { school: 'Music, Theatre and Dance, Visual Arts', requirement: 'An optional portfolio or audition video, reviewed by the department. You can be admitted without it.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application (one filing period)', deadline: 'November 30', decision: 'By mid-March', binding: false },
        ],
        note: 'You apply on the UC application, which opens August 1. Selected applicants are invited to join the waitlist and must opt in by April 15.',
        source: 'timeline',
        binding: 'Nothing is binding. For fall 2027 the University of California has one filing period, October 1 to November 30, with admission decisions in March.',
        bindingSource: 'ucDates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UC San Diego is test-blind: SAT and ACT scores are not considered in admission or scholarship decisions, even if you send them. Scores you send may be used only to meet minimum requirements or for course placement if you enroll.',
        source: 'requirements',
        cdsLine: 'The Common Data Set agrees: it marks the SAT and ACT as not considered for admission, even if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'timeline',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees (including the health fee)', amounts: [21519, 21519] },
            { item: 'Nonresident supplemental tuition', amounts: [0, 39270] },
            { item: 'Housing (on campus)', amounts: [13761, 13761] },
            { item: 'Food', amounts: [6981, 6981] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1236, 1236] },
            { item: 'Miscellaneous personal expenses', amounts: [2838, 2838] },
            { item: 'Transportation', amounts: [948, 948] },
        ],
        totals: [47283, 86553],
        note: 'The budget for a student entering in fall 2026 and living on campus. Tuition and fees include a $3,195 health fee that can be waived with equivalent private insurance.',
        source: 'coa',
        netPrice: {
            average: 14794,
            byIncome: [
                { income: '$0 to $30,000', amount: 9229 },
                { income: '$30,001 to $48,000', amount: 9965 },
                { income: '$48,001 to $75,000', amount: 12147 },
                { income: '$75,001 to $110,000', amount: 17235 },
                { income: 'Over $110,000', amount: 32338 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UC San Diego\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
